export const visualizeContent = {
  nl: {
    seoTitle: 'Activiteitenatlas · Perpetual Plexus visualisaties',
    seoDescription:
      'Live beelden van het Perpetual Plexus-netwerk: stroom, cumulatieve cirkelklok, rimpelingen en hartslag, getekend uit realtime bewegingstriggers.',
    title: 'Visualisatie',
    subtitle: 'Activiteitenatlas',
    headingBefore: 'Elke beweging, elk ',
    headingAccent: 'signaal',
    headingAfter: ', weergegeven als licht.',
    lead: 'Een levende atlas van het Perpetual Plexus netwerk.',
    loading: 'Triggers laden…',
    footer: 'Data live gestreamd vanuit Lovable Cloud',
    sections: {
      stroom: {
        title: 'Stroom',
        desc: 'Gestapelde stromen — één band per lichtsculptuur, vloeiend door het tijdvenster.',
      },
      klok: {
        title: 'Cumulatieve cirkelklok',
        desc: 'De volledige activiteit van het netwerk gecentreerd als één cirkel van 24 uur.',
      },
      rimpelingen: {
        title: 'Rimpelingen',
        desc: 'Het netwerk van bovenaf gezien. Elk lichtsculptuur is een druppelpunt; elk contactmoment verbreedt de kringen.',
      },
      hartslag: {
        title: 'Hartslag',
        desc: 'Tekenen van leven, de momenten waarop een lichtsculptuur zich meldde.',
      },
      mycelium: {
        title: 'Mycelium',
        desc: 'Het netwerk als schimmeldraden. Reageren twee lichtsculpturen binnen dertig seconden achter elkaar, dan groeit er een draad tussen hen; paren die vaak terugkeren worden een dikke streng. Een interactie vraagt twee sculpturen, een contactmoment één — daarom is dit aantal lager dan dat van de cirkelklok.',
      },
      puls: {
        title: 'Hartslagritme',
        desc: 'Elke check-in van een lichtsculptuur als een stoot op de hartfilm, per tijdsvenster.',
      },
      signaal: {
        title: 'Signaalveld',
        desc: 'De WiFi-signaalsterkte van elke lichtsculptuur in de tijd — hoe sterker het signaal, hoe hoger de golf.',
      },
      ademhaling: {
        title: 'Ademhaling',
        desc: 'De uptime van elke lichtsculptuur als zaagtand — elke lijn stijgt een etmaal en valt bij de dagelijkse reset.',
      },
      weefsel: {
        title: 'Patroonweefsel',
        desc: 'Welk lichtpatroon wanneer actief was — een weefsel van gekleurde banden, één per lichtsculptuur.',
      },
    },
    charts: {
      signals: 'Signalen',
      sculptures: 'Lichtsculpturen',
      triggers: 'triggers',
      beats: 'hartslagen',
      noActivity: 'Geen activiteit in deze periode',
      riverAria: 'Stroomrivier van lichtsculptuuractiviteit',
      pulseAria: 'Hartfilm van lichtsculptuur-check-ins',
      signalAria: 'WiFi-signaalsterkte per lichtsculptuur in de tijd',
      breathAria: 'Uptime-zaagtand per lichtsculptuur',
      tapestryAria: 'Patroongeschiedenis per lichtsculptuur',
      sculptureFallback: (n: string) => `Lichtsculptuur ${n}`,
      windows: {
        '1h': 'Afgelopen uur',
        '24h': 'Afgelopen dag',
        '7d': 'Afgelopen week',
        '30d': 'Afgelopen maand',
        '1y': 'Afgelopen jaar',
        '10y': 'Afgelopen decennium',
      },
      clockFolded: (days: number) => `Activiteit afgelopen ${days} dag${days === 1 ? '' : 'en'}`,
      clockTriggers: (n: number) => `${n.toLocaleString('nl-NL')} Contactmoment${n === 1 ? '' : 'en'}`,
      clockCaption: () =>
        `Eén ring per lichtsculptuur`,
      pondCaption: (total: string, max: string) =>
        `Eén druppel per lichtsculptuur · rimpelbreedte ∝ aantal triggers · ${total} triggers, drukste lichtsculptuur: ${max}`,
      myceliumCaption: (sculptures: number, interactions: number) =>
        `${sculptures} lichtsculpturen · ${interactions.toLocaleString('nl-NL')} interacties`,
      myceliumAria: 'Myceliumnetwerk van interacties tussen lichtsculpturen',
      heartbeatMissing: 'De tabel device_heartbeats bestaat nog niet. Voer docs/sql/device_heartbeats.sql uit in de SQL-editor van je database, upload daarna de nieuwe firmware — vanaf dat moment bouwt deze grafiek zich op.',
      metricsMissing: 'Deze grafiek heeft de databasefuncties uit docs/sql/viz_metric_functions.sql nodig. Voer dat bestand eenmalig uit in de SQL-editor van de database — daarna verschijnt hier de volledige geschiedenis.',
      heartbeatLoading: 'Hartslagen laden…',
      heartbeatEmpty:
        'Nog geen hartslagen vastgelegd. Zodra de lichtsculpturen de nieuwe firmware draaien verschijnt hier per dag een blokje.',
      yearAgo: '1 jaar geleden',
      today: 'vandaag',
      heartbeatFooter: (devices: number, beats: number) => `${devices} lichtsculpturen · ${beats} hartslagen`,
    },
  },

  en: {
    seoTitle: 'Activity atlas · Perpetual Plexus visualizations',
    seoDescription:
      'Live imagery of the Perpetual Plexus network: flow, cumulative circle clock, ripples and heartbeat, drawn from realtime motion triggers.',
    title: 'Visualization',
    subtitle: 'Activity atlas',
    headingBefore: 'Every movement, every ',
    headingAccent: 'signal',
    headingAfter: ', rendered as light.',
    lead: 'A living atlas of the network.',
    loading: 'Loading triggers…',
    footer: 'Data streamed live from Lovable Cloud',
    sections: {
      stroom: {
        title: 'Flow',
        desc: 'Stacked streams — one band per light sculpture, flowing through the time window.',
      },
      klok: {
        title: 'Cumulative circle clock',
        desc: 'The full activity of the network folded into a single 24-hour circle.',
      },
      rimpelingen: {
        title: 'Ripples',
        desc: 'The network seen from above. Every light sculpture is a point of impact; every encounter widens the rings.',
      },
      hartslag: {
        title: 'Heartbeat',
        desc: 'Signs of life — the moments a light sculpture reported in.',
      },
      puls: {
        title: 'Heartbeat rhythm',
        desc: 'Every check-in of a light sculpture as a beat on the heart monitor, per time window.',
      },
      signaal: {
        title: 'Signal field',
        desc: 'The WiFi signal strength of every light sculpture over time — the stronger the signal, the higher the wave.',
      },
      mycelium: {
        title: 'Mycelium',
        desc: 'The network as fungal threads. When two light sculptures respond within thirty seconds of each other, a thread grows between them; pairs that keep returning become a thick strand. An interaction takes two sculptures, a contact moment one — which is why this count stays lower than the circle clock\'s.',
      },
      ademhaling: {
        title: 'Breathing',
        desc: 'The uptime of every light sculpture as a sawtooth — every line rises for a day and falls at the daily reset.',
      },
      weefsel: {
        title: 'Pattern tapestry',
        desc: 'Which light pattern was active when — a tapestry of coloured bands, one per light sculpture.',
      },
    },
    charts: {
      signals: 'Signals',
      sculptures: 'Light sculptures',
      triggers: 'triggers',
      beats: 'heartbeats',
      noActivity: 'No activity in this period',
      riverAria: 'Flow river of light sculpture activity',
      pulseAria: 'Heart monitor of light sculpture check-ins',
      signalAria: 'WiFi signal strength per light sculpture over time',
      breathAria: 'Uptime sawtooth per light sculpture',
      tapestryAria: 'Pattern history per light sculpture',
      sculptureFallback: (n: string) => `Light sculpture ${n}`,
      windows: {
        '1h': 'Past hour',
        '24h': 'Past day',
        '7d': 'Past week',
        '30d': 'Past month',
        '1y': 'Past year',
        '10y': 'Past decade',
      },
      clockFolded: (days: number) => `Activity past ${days} day${days === 1 ? '' : 's'}`,
      clockTriggers: (n: number) => `${n.toLocaleString('en-US')} contact moment${n === 1 ? '' : 's'}`,
      clockCaption: () =>
        `One ring per light sculpture`,
      pondCaption: (total: string, max: string) =>
        `One drop per light sculpture · ripple width ∝ number of triggers · ${total} triggers, busiest light sculpture: ${max}`,
      myceliumCaption: (sculptures: number, interactions: number) =>
        `${sculptures} light sculptures · ${interactions.toLocaleString('en-US')} interactions`,
      myceliumAria: 'Mycelium network of interactions between light sculptures',
      heartbeatMissing: 'The device_heartbeats table does not exist yet. Run docs/sql/device_heartbeats.sql in your database SQL editor, then upload the new firmware — this chart starts building from that moment.',
      metricsMissing: 'This chart needs the database functions from docs/sql/viz_metric_functions.sql. Run that file once in your database SQL editor — the full history will appear here.',
      heartbeatLoading: 'Loading heartbeats…',
      heartbeatEmpty:
        'No heartbeats recorded yet. Once the light sculptures run the new firmware, a block appears here for every day.',
      yearAgo: '1 year ago',
      today: 'today',
      heartbeatFooter: (devices: number, beats: number) => `${devices} light sculptures · ${beats} heartbeats`,
    },
  },
} as const;

